import { z } from "zod";

// Erro de um campo de entrada: `path` em notação de ponto e índices
// (`contacts.0.email`), "" para o erro do formulário. `code` segue o vocabulário
// da convenção H2J (`required`, `invalid_format`, `invalid_value`, `too_short`,
// `too_long`, `too_small`, `too_large`, `not_unique`).
export const errorFieldSchema = z.object({
  path: z.string(),
  code: z.string().min(1),
  message: z.string(),
});

export type ErrorField = z.infer<typeof errorFieldSchema>;

// Standard error shape used across SenseGuard (API + Web + Worker)
export const standardErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  status: z.number().int().optional(),
  requestId: z.string().optional(),
  details: z.unknown().optional(),
  fields: z.array(errorFieldSchema).optional(),
});

export type StandardError = z.infer<typeof standardErrorSchema>;

export const standardErrorResponseSchema = z.object({
  error: standardErrorSchema,
});

export type StandardErrorResponse = z.infer<typeof standardErrorResponseSchema>;

export function toStandardError(err: unknown, fallback?: Partial<StandardError>): StandardError {
  if (standardErrorSchema.safeParse(err).success) {
    return err as StandardError;
  }

  const base: StandardError = {
    code: fallback?.code ?? "UNKNOWN",
    message: fallback?.message ?? "Unknown error",
    status: fallback?.status,
    requestId: fallback?.requestId,
    details: fallback?.details,
    fields: fallback?.fields,
  };

  if (typeof err === "string" && err.trim().length > 0) {
    return { ...base, message: err };
  }

  if (err instanceof Error) {
    return {
      ...base,
      message: err.message || base.message,
      details: {
        ...(typeof base.details === "object" && base.details ? (base.details as object) : {}),
        name: err.name,
        // NOTE: stack can be useful in dev; keep it in details and let the caller decide
        stack: err.stack,
      },
    };
  }

  if (err && typeof err === "object") {
    const maybeMessage = (err as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim().length > 0) {
      return { ...base, message: maybeMessage, details: err };
    }

    return { ...base, details: err };
  }

  return { ...base, message: String(err) };
}

export function formatStandardErrorMessage(err: unknown): string {
  const e = toStandardError(err);
  return e.code ? `[${e.code}] ${e.message}` : e.message;
}
